import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Lock, Eye, EyeOff, Loader2, Check, AlertTriangle, Info, Mail } from 'lucide-react'

interface PINModalProps {
  isOpen: boolean
  onClose: () => void
}

const getAuthToken = () => localStorage.getItem('niki_access_token')
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1'

interface PINStatus {
  pin_set: boolean
  pin_enabled: boolean
}

export function PINModal({ isOpen, onClose }: PINModalProps) {
  const [status, setStatus] = useState<PINStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'set' | 'change' | 'disable'>('set')

  // Set PIN
  const [newPin, setNewPin] = useState('')
  const [confirmPin, setConfirmPin] = useState('')
  const [showNewPin, setShowNewPin] = useState(false)
  const [showConfirmPin, setShowConfirmPin] = useState(false)

  // Change PIN
  const [currentPin, setCurrentPin] = useState('')
  const [changeNewPin, setChangeNewPin] = useState('')
  const [changeConfirmPin, setChangeConfirmPin] = useState('')
  const [showCurrentPin, setShowCurrentPin] = useState(false)
  const [showChangeNewPin, setShowChangeNewPin] = useState(false)
  const [showChangeConfirmPin, setShowChangeConfirmPin] = useState(false)

  // Disable PIN
  const [otp, setOtp] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [otpCode, setOtpCode] = useState<string | null>(null)

  // UI States
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    if (isOpen) {
      fetchPINStatus()
    }
  }, [isOpen])

  useEffect(() => {
    if (status) {
      if (!status.pin_set) {
        setActiveTab('set')
      } else if (status.pin_enabled) {
        setActiveTab('change')
      } else {
        setActiveTab('disable')
      }
    }
  }, [status])

  const fetchPINStatus = async () => {
    try {
      const res = await fetch(`${API_BASE}/pin/status`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      })

      if (!res.ok) throw new Error('Failed to fetch PIN status')

      const data = await res.json()
      setStatus(data)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSetPIN = async () => {
    alert('[DEBUG] handleSetPIN called!')  // TEMPORARY DEBUG
    console.log('[handleSetPIN] Function called')
    console.log(`[handleSetPIN] newPin: "${newPin}" (length: ${newPin.length})`)
    console.log(`[handleSetPIN] confirmPin: "${confirmPin}" (length: ${confirmPin.length})`)

    if (newPin.length < 4 || newPin.length > 6) {
      console.log('[handleSetPIN] Validation failed: PIN length invalid')
      setError('PIN must be 4-6 digits')
      return
    }

    if (newPin !== confirmPin) {
      console.log('[handleSetPIN] Validation failed: PINs do not match')
      setError('PINs do not match')
      return
    }

    console.log('[handleSetPIN] Validation passed, starting API call...')
    setSaving(true)
    setError('')

    try {
      const token = getAuthToken()
      console.log(`[handleSetPIN] Auth token: ${token ? 'present' : 'MISSING'}`)
      console.log(`[handleSetPIN] API URL: ${API_BASE}/pin/set`)

      const res = await fetch(`${API_BASE}/pin/set`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ pin: newPin }),
      })

      console.log(`[handleSetPIN] Response status: ${res.status}`)

      if (!res.ok) {
        const err = await res.json()
        console.log(`[handleSetPIN] API error:`, err)
        throw new Error(err.detail || 'Failed to set PIN')
      }

      console.log('[handleSetPIN] PIN set successfully!')
      setSuccess('PIN set successfully!')
      setNewPin('')
      setConfirmPin('')

      setTimeout(() => {
        fetchPINStatus()
        setSuccess('')
      }, 2000)
    } catch (err: any) {
      console.error('[handleSetPIN] Error:', err)
      setError(err.message)
    } finally {
      setSaving(false)
      console.log('[handleSetPIN] Function complete')
    }
  }

  const handleChangePIN = async () => {
    if (changeNewPin.length < 4 || changeNewPin.length > 6) {
      setError('New PIN must be 4-6 digits')
      return
    }

    if (changeNewPin !== changeConfirmPin) {
      setError('New PINs do not match')
      return
    }

    setSaving(true)
    setError('')

    try {
      const res = await fetch(`${API_BASE}/pin/change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          current_pin: currentPin,
          new_pin: changeNewPin,
        }),
      })

      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.detail || 'Failed to change PIN')
      }

      setSuccess('PIN changed successfully!')
      setCurrentPin('')
      setChangeNewPin('')
      setChangeConfirmPin('')

      setTimeout(() => {
        setSuccess('')
      }, 2000)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleRequestOTP = async () => {
    setSaving(true)
    setError('')

    try {
      const res = await fetch(`${API_BASE}/pin/disable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({}),
      })

      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.detail || 'Failed to send OTP')
      }

      const data = await res.json()
      setOtpSent(true)
      // In dev, OTP is returned in response
      if (data.otp) {
        setOtpCode(data.otp)
      }
      setSuccess(data.message)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDisablePIN = async () => {
    if (otp.length !== 6) {
      setError('OTP must be 6 digits')
      return
    }

    setSaving(true)
    setError('')

    try {
      const res = await fetch(`${API_BASE}/pin/disable/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({ otp }),
      })

      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.detail || 'Failed to disable PIN')
      }

      setSuccess('PIN requirement disabled successfully!')
      setOtp('')
      setOtpSent(false)

      setTimeout(() => {
        fetchPINStatus()
        setSuccess('')
      }, 2000)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleEnablePIN = async () => {
    setSaving(true)
    setError('')

    try {
      const res = await fetch(`${API_BASE}/pin/enable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({}),
      })

      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.detail || 'Failed to enable PIN')
      }

      setSuccess('PIN requirement enabled successfully!')

      setTimeout(() => {
        fetchPINStatus()
        setSuccess('')
      }, 2000)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#1a2e1a] rounded-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/20 rounded-lg">
                <Lock className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-semibold text-white">Niki PIN</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white/70" />
            </button>
          </div>

          {/* Important Notice */}
          <div className="m-4 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-400">
              <p className="font-semibold mb-1">Important: This is your Niki PIN</p>
              <p className="text-blue-400/80">
                This PIN is for authorizing transactions within Niki. This is NOT your M-Pesa PIN.
                Never share your M-Pesa PIN with anyone.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="p-12 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : (
            <>
              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Tabs */}
                <div className="flex gap-2 bg-white/5 rounded-lg p-1">
                  {status?.pin_set && (
                    <button
                      onClick={() => setActiveTab('change')}
                      className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        activeTab === 'change'
                          ? 'bg-primary text-white'
                          : 'text-white/60 hover:text-white/80'
                      }`}
                    >
                      Change PIN
                    </button>
                  )}
                  {!status?.pin_set && (
                    <button
                      onClick={() => setActiveTab('set')}
                      className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        activeTab === 'set'
                          ? 'bg-primary text-white'
                          : 'text-white/60 hover:text-white/80'
                      }`}
                    >
                      Set PIN
                    </button>
                  )}
                  {status?.pin_set && (
                    <button
                      onClick={() => setActiveTab('disable')}
                      className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        activeTab === 'disable'
                          ? 'bg-primary text-white'
                          : 'text-white/60 hover:text-white/80'
                      }`}
                    >
                      {status.pin_enabled ? 'Disable' : 'Enable'}
                    </button>
                  )}
                </div>

                {/* Set PIN Tab */}
                {activeTab === 'set' && !status?.pin_set && (
                  <div className="space-y-4">
                    <p className="text-sm text-white/70">
                      Set a 4-6 digit PIN to secure sensitive transactions in Niki.
                    </p>

                    <div>
                      <label className="text-sm text-white/70 mb-2 block">New PIN</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <input
                          type={showNewPin ? 'text' : 'password'}
                          value={newPin}
                          onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="Enter 4-6 digit PIN"
                          className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-12 py-3 text-white text-center tracking-widest placeholder-white/40 focus:outline-none focus:border-primary/50"
                          maxLength={6}
                          inputMode="numeric"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPin(!showNewPin)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                        >
                          {showNewPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm text-white/70 mb-2 block">Confirm PIN</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <input
                          type={showConfirmPin ? 'text' : 'password'}
                          value={confirmPin}
                          onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="Confirm PIN"
                          className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-12 py-3 text-white text-center tracking-widest placeholder-white/40 focus:outline-none focus:border-primary/50"
                          maxLength={6}
                          inputMode="numeric"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPin(!showConfirmPin)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                        >
                          {showConfirmPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleSetPIN}
                      disabled={newPin.length < 4 || newPin !== confirmPin || saving}
                      className="w-full px-4 py-3 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Setting...
                        </>
                      ) : (
                        'Set PIN'
                      )}
                    </button>
                  </div>
                )}

                {/* Change PIN Tab */}
                {activeTab === 'change' && status?.pin_set && (
                  <div className="space-y-4">
                    <p className="text-sm text-white/70">
                      Enter your current PIN and choose a new one.
                    </p>

                    <div>
                      <label className="text-sm text-white/70 mb-2 block">Current PIN</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <input
                          type={showCurrentPin ? 'text' : 'password'}
                          value={currentPin}
                          onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="Enter current PIN"
                          className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-12 py-3 text-white text-center tracking-widest placeholder-white/40 focus:outline-none focus:border-primary/50"
                          maxLength={6}
                          inputMode="numeric"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPin(!showCurrentPin)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                        >
                          {showCurrentPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm text-white/70 mb-2 block">New PIN</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <input
                          type={showChangeNewPin ? 'text' : 'password'}
                          value={changeNewPin}
                          onChange={(e) => setChangeNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="Enter new PIN"
                          className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-12 py-3 text-white text-center tracking-widest placeholder-white/40 focus:outline-none focus:border-primary/50"
                          maxLength={6}
                          inputMode="numeric"
                        />
                        <button
                          type="button"
                          onClick={() => setShowChangeNewPin(!showChangeNewPin)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                        >
                          {showChangeNewPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm text-white/70 mb-2 block">Confirm New PIN</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <input
                          type={showChangeConfirmPin ? 'text' : 'password'}
                          value={changeConfirmPin}
                          onChange={(e) => setChangeConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="Confirm new PIN"
                          className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-12 py-3 text-white text-center tracking-widest placeholder-white/40 focus:outline-none focus:border-primary/50"
                          maxLength={6}
                          inputMode="numeric"
                        />
                        <button
                          type="button"
                          onClick={() => setShowChangeConfirmPin(!showChangeConfirmPin)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                        >
                          {showChangeConfirmPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleChangePIN}
                      disabled={currentPin.length < 4 || changeNewPin.length < 4 || changeNewPin !== changeConfirmPin || saving}
                      className="w-full px-4 py-3 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Changing...
                        </>
                      ) : (
                        'Change PIN'
                      )}
                    </button>
                  </div>
                )}

                {/* Disable/Enable PIN Tab */}
                {activeTab === 'disable' && status?.pin_set && (
                  <div className="space-y-4">
                    {status.pin_enabled ? (
                      <>
                        <p className="text-sm text-white/70">
                          Disable PIN requirement for transactions. You'll receive an OTP via SMS to confirm.
                        </p>

                        {!otpSent ? (
                          <button
                            type="button"
                            onClick={handleRequestOTP}
                            disabled={saving}
                            className="w-full px-4 py-3 rounded-lg bg-yellow-600 text-white font-medium hover:bg-yellow-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {saving ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Sending OTP...
                              </>
                            ) : (
                              <>
                                <Mail className="w-4 h-4" />
                                Send OTP
                              </>
                            )}
                          </button>
                        ) : (
                          <>
                            {otpCode && (
                              <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                <p className="text-xs text-white/50 mb-1">Development OTP:</p>
                                <p className="text-2xl font-mono text-primary tracking-widest">{otpCode}</p>
                              </div>
                            )}

                            <div>
                              <label className="text-sm text-white/70 mb-2 block">Enter OTP</label>
                              <input
                                type="text"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="6-digit OTP"
                                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-center tracking-widest placeholder-white/40 focus:outline-none focus:border-primary/50"
                                maxLength={6}
                                inputMode="numeric"
                              />
                            </div>

                            <button
                              type="button"
                              onClick={handleDisablePIN}
                              disabled={otp.length !== 6 || saving}
                              className="w-full px-4 py-3 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                              {saving ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Disabling...
                                </>
                              ) : (
                                'Disable PIN'
                              )}
                            </button>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-white/70">
                          PIN requirement is currently disabled. Enable it to require PIN for sensitive transactions.
                        </p>

                        <button
                          type="button"
                          onClick={handleEnablePIN}
                          disabled={saving}
                          className="w-full px-4 py-3 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {saving ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Enabling...
                            </>
                          ) : (
                            'Enable PIN'
                          )}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}

                {/* Success */}
                {success && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-3 flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <p className="text-green-400 text-sm">{success}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
